import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, BellRing } from "lucide-react";

/**
 * Item 4 (2026-07): "Up next" — the next 1-2 items from the calendar feed
 * (was "Coming up", trimmed from 3), with the local-alerts radius control
 * folded in as a compact footer row ("Alerts: within 5 miles · change").
 * Same data source as /calendar; the user's own commitments rank first.
 */
export function UpNextCard({ apps, hosted, user, alertRadius, saveAlerts }) {
  const [events, setEvents] = useState(null);
  const [editingAlerts, setEditingAlerts] = useState(false);

  useEffect(() => {
    api.calendarEvents(8).then(setEvents).catch(() => setEvents([]));
  }, []);

  const appStatus = new Map(
    (apps || []).filter((a) => ["applied", "accepted"].includes(a.status)).map((a) => [a.opportunity_id, a.status])
  );
  const appliedIds = new Set(appStatus.keys());
  const hostedIds = new Set((hosted || []).map((h) => h.id));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const relevance = (e) => (e.is_gathering || appliedIds.has(e.id) || hostedIds.has(e.id) ? 0 : 1);
  const upcoming = (events || [])
    .map((e) => ({ ...e, _d: new Date(e.occurrence_date || e.event_date) }))
    .filter((e) => e._d >= today)
    .sort((a, b) => relevance(a) - relevance(b) || a._d - b._d)
    .slice(0, 2);

  const tag = (e) => {
    if (hostedIds.has(e.id)) return "Hosting";
    if (appliedIds.has(e.id)) return appStatus.get(e.id) === "accepted" ? "Going" : "Applied";
    if (e.is_gathering) return "Gathering";
    return null;
  };

  const radiusLabel = Number(alertRadius) > 0
    ? `within ${alertRadius} mile${alertRadius === "1" ? "" : "s"}`
    : "off";

  return (
    <div data-testid="coming-up-card" className="mt-6 bg-white border border-neutral-200 rounded-xl p-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-terracotta" /> Up next
        </div>
        <Link data-testid="coming-up-view-calendar" to="/calendar" className="text-xs text-forest hover:underline font-medium">
          View calendar →
        </Link>
      </div>
      {events && upcoming.length === 0 ? (
        <div data-testid="up-next-empty" className="mt-3 text-sm text-neutral-500">
          Nothing scheduled yet — <Link to="/opportunities" className="text-forest underline">browse opportunities</Link> to line something up.
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {upcoming.map((e) => (
            <Link
              key={`${e.id}-${e._d.toISOString()}`}
              data-testid={`coming-up-item-${e.id}`}
              to={e.is_gathering ? `/collectives/${e.collective_id}` : `/opportunities/${e.id}`}
              className="flex items-center gap-3 border border-neutral-200 rounded-xl px-4 py-3 hover:border-forest/40 transition-colors"
            >
              <span className="shrink-0 w-12 text-center rounded-lg bg-forest/5 border border-forest/10 py-1">
                <span className="block text-[10px] uppercase tracking-wide text-forest/60 font-bold">
                  {e._d.toLocaleDateString([], { month: "short" })}
                </span>
                <span className="block font-display text-lg text-forest leading-tight">{e._d.getDate()}</span>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-forest truncate">{e.title}</span>
                <span className="block text-xs text-neutral-500 truncate">
                  {e.is_gathering ? e.collective_name : e.org_name}
                  {tag(e) && <span className="ml-1.5 text-terracotta font-medium">· {tag(e)}</span>}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
      {/* Local-alerts control, folded in as this card's footer row */}
      <div data-testid="upnext-alerts-row" className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-2 text-xs text-neutral-600 flex-wrap">
        <BellRing className="w-3.5 h-3.5 text-terracotta shrink-0" />
        {user.lat == null ? (
          <span>
            Local alerts need a location — <Link to="/onboarding" data-testid="alerts-profile-link" className="text-forest underline">set yours</Link>
          </span>
        ) : editingAlerts ? (
          <Select value={alertRadius} onValueChange={(v) => { saveAlerts(v); setEditingAlerts(false); }}>
            <SelectTrigger data-testid="alert-radius-select" className="w-40 h-8 rounded-full text-xs">
              <SelectValue placeholder="Radius" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Off</SelectItem>
              <SelectItem value="1">Within 1 mile</SelectItem>
              <SelectItem value="3">Within 3 miles</SelectItem>
              <SelectItem value="5">Within 5 miles</SelectItem>
              <SelectItem value="10">Within 10 miles</SelectItem>
              <SelectItem value="25">Within 25 miles</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <>
            <span data-testid="alerts-summary">Alerts: {radiusLabel}</span>
            <span className="text-neutral-300">·</span>
            <button data-testid="alerts-change-btn" onClick={() => setEditingAlerts(true)} className="text-forest underline">
              change
            </button>
          </>
        )}
      </div>
    </div>
  );
}
